import {
  Partner,
  PlatformEntity,
  RecordFolder,
  RecordFolderAssignment,
} from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { OPTIMISTIC_ID_PREFIX } from 'src/graphql/entities/custom-view';
import {
  CREATE_RECORD_FOLDER_ASSIGNMENT_QUERY,
  GET_RECORD_FOLDER_ASSIGNMENT_QUERY,
} from 'src/graphql/entities/record-folder-assignment/record-folder-assignment.operations';
import { QueryVariables } from 'src/graphql/types';
import {
  GetRecordFolderAssignment,
  getRecordFolderAssignmentQueryParams,
} from './use-record-folder-assignments';

export interface CreateRecordFolderAssignment {
  createRecordFolderAssignment: RecordFolderAssignment[];
}

interface UseCreateRecordFolderAssignmentOptions {
  entityFilter?: string[];
}

interface CreateRecordFolderAssignmentOptions {
  omitIsolationModeHeader?: boolean;
}

export const useCreateRecordFolderAssignment = (
  options?: UseCreateRecordFolderAssignmentOptions,
) => {
  const entityFilter = options?.entityFilter;
  const [createRecordFolderAssignment, { loading: isLoading }] =
    useMutation<CreateRecordFolderAssignment>(CREATE_RECORD_FOLDER_ASSIGNMENT_QUERY, {
      update(cache, { data }) {
        if (!data?.createRecordFolderAssignment?.length) return;

        cache.updateQuery<GetRecordFolderAssignment, QueryVariables>(
          {
            query: GET_RECORD_FOLDER_ASSIGNMENT_QUERY,
            variables: {
              params: getRecordFolderAssignmentQueryParams(entityFilter),
            },
          },
          (existingData) => {
            if (!existingData) return existingData;

            return {
              ...existingData,
              getRecordFolderAssignment: {
                ...existingData.getRecordFolderAssignment,
                records: [
                  data.createRecordFolderAssignment[0],
                  ...existingData.getRecordFolderAssignment.records.filter(
                    (i) =>
                      i.recordFolderAssignmentId !==
                        data.createRecordFolderAssignment[0].recordFolderAssignmentId &&
                      !i.recordFolderAssignmentId.startsWith(OPTIMISTIC_ID_PREFIX),
                  ),
                ],
              },
            };
          },
        );
      },
    });

  const createWithOptimisticResponse = useCallback(
    (
      data: Omit<
        RecordFolderAssignment,
        | 'id'
        | 'recordFolderAssignmentId'
        | 'createdAt'
        | 'updatedAt'
        | 'userId'
        | 'partner'
        | 'platformUser'
        | 'labelAssignments'
        | 'platformEntity'
        | 'recordFolder'
      >,
      options?: CreateRecordFolderAssignmentOptions,
    ) => {
      return createRecordFolderAssignment({
        variables: { data },
        context: options?.omitIsolationModeHeader
          ? { omitIsolationModeHeader: true }
          : undefined,
        optimisticResponse() {
          const nowString = new Date().toISOString();
          const tempId = `${OPTIMISTIC_ID_PREFIX}${nowString}`;

          return {
            createRecordFolderAssignment: [
              {
                __typename: 'RecordFolderAssignment',
                ...data,
                id: tempId,
                recordFolderAssignmentId: tempId,
                createdAt: nowString,
                // below properties are not used by apollo, they are needed for ts purposes
                labelAssignments: [],
                recordFolder: {} as RecordFolder,
                platformEntity: {} as PlatformEntity,
                partner: {} as Partner,
              },
            ],
          };
        },
      });
    },
    [createRecordFolderAssignment],
  );

  return {
    createRecordFolderAssignment: createWithOptimisticResponse,
    isLoading,
  };
};
